// external
import type { FC } from 'react';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RESIDUAL_PERCENT } from '@makemydeal/dr-platform-types';
import type { ResidualValueType } from '@makemydeal/dr-platform-types';

// internal components
import { ResidualBreakdown } from './ResidualBreakdownTab';

// types
import type { ResidualErrors, ResidualData } from './mileageCostOptionsTypes';

// validation
import { validateResidualBasedOn, validateProgramResidual, validateProgramResidualPercent } from './residualValidation';

// constants
import { RESIDUAL_FIELD_LABELS } from './constants';

// selectors
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

// actions
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';

export const ResidualBreakdownTabContent: FC = () => {
    // Residual selectors
    const baseResidualDollar = useSelector(offerReduxSelectors.getBaseResidualDollar);
    const baseResidualPercent = useSelector(offerReduxSelectors.getBaseResidualPercent);
    const adjustedResidualValue = useSelector(offerReduxSelectors.getAdjustedResidualValue);
    const retailPrice = useSelector(offerSelectors.getRetailPriceOverrideWithFallback);
    const residualInfoOverride = useSelector(offerSelectors.getResidualInfoOverrideWithFallback);
    const isAutoSelectUnchecked = useSelector(offerReduxSelectors.getIsUserProgramQuotes);

    const isLeaseResidualTreatmentEnabled = useSelector(featureToggleSelectors.isLeaseResidualTreatmentEnabled);
    const residualControlsDisabled = isLeaseResidualTreatmentEnabled ? false : !isAutoSelectUnchecked;
    const isExplicitLenderP1Enabled = useSelector(featureToggleSelectors.isExplicitLenderP1Enabled);
    const isUnifiedResidualFieldsEnabled = useSelector(featureToggleSelectors.isUnifiedResidualFieldsEnabled);
    const residualTypeFromPayment = useSelector(offerReduxSelectors.getResidualType);
    // Payment responses drive the type; fall back to the type saved with the deal (e.g. on load). Gate on,
    // the dealer's selection comes first: the toggle no longer prices, so the response's type never moves.
    const displayedResidualType =
        (isExplicitLenderP1Enabled
            ? residualInfoOverride?.baseResidualType ?? residualTypeFromPayment
            : residualTypeFromPayment ?? residualInfoOverride?.baseResidualType) ?? RESIDUAL_PERCENT;
    const dispatch = useDispatch();

    const handleResidualTypeChange = (type: ResidualValueType) => {
        // Re-displaying the residual is not an edit, so it prices nothing.
        dispatch(offerReduxActionCreators.updateResidualTabDisplayType(type, !isExplicitLenderP1Enabled));
    };

    // Get direct program residual values
    const programResidualValue = useSelector(offerReduxSelectors.getProgramResidual);
    const programResidualPercentValue = useSelector(offerReduxSelectors.getProgramResidualPercent);

    // Get total accessories residual from selected term
    const residualizedAccessoriesValue = useSelector(offerReduxSelectors.getTotalAccessoriesResidual);

    // Get mileage-related values needed for residual calculations
    const initialMileageCharge = useSelector(offerReduxSelectors.getInitialMileageCharge);
    const excessAmount = useSelector(offerReduxSelectors.getExcessAmount);

    // Calculate program adjustment percent
    /* istanbul ignore next -- defensive fallbacks for edge cases */
    const programAdjustmentPercent = useMemo(() => {
        const programPercent = programResidualPercentValue ?? residualInfoOverride?.programResidualPercent;
        if (baseResidualPercent != null && programPercent != null) {
            return programPercent - baseResidualPercent;
        }
        return undefined;
    }, [baseResidualPercent, programResidualPercentValue, residualInfoOverride?.programResidualPercent]);

    // Build residual data from selectors (read-only display)
    /* istanbul ignore next -- defensive fallbacks for edge cases */
    const residualData: ResidualData = useMemo(
        () => ({
            residualBasedOn: retailPrice,
            baseResidualDollar: baseResidualDollar,
            baseResidualPercent: baseResidualPercent,
            programAdjustmentPercent: programAdjustmentPercent,
            programResidual: programResidualValue ?? residualInfoOverride?.programResidual,
            programResidualPercent: programResidualPercentValue ?? residualInfoOverride?.programResidualPercent,
            existingMileageCost: initialMileageCharge,
            prepaidMileageCost: excessAmount,
            residualizedAccessories: residualizedAccessoriesValue,
            adjustedResidual: adjustedResidualValue
        }),
        [
            retailPrice,
            baseResidualDollar,
            baseResidualPercent,
            programAdjustmentPercent,
            programResidualValue,
            programResidualPercentValue,
            residualInfoOverride?.programResidual,
            residualInfoOverride?.programResidualPercent,
            initialMileageCharge,
            excessAmount,
            residualizedAccessoriesValue,
            adjustedResidualValue
        ]
    );

    // Validation (for display purposes only since read-only)
    /* istanbul ignore next -- defensive fallbacks for edge cases */
    const residualErrors: ResidualErrors = useMemo(() => {
        const newErrors: ResidualErrors = {};

        const residualBasedOnResult = validateResidualBasedOn(residualData.residualBasedOn ?? null);
        if (!residualBasedOnResult.isValid) {
            newErrors.residualBasedOn = residualBasedOnResult.error;
        }

        const programResidualResult = validateProgramResidual(
            residualData.programResidual ?? null,
            isUnifiedResidualFieldsEnabled ? RESIDUAL_FIELD_LABELS.RESIDUAL : undefined
        );
        if (!programResidualResult.isValid) {
            newErrors.programResidual = programResidualResult.error;
        }

        const programResidualPercentResult = validateProgramResidualPercent(
            residualData.programResidualPercent ?? null,
            isUnifiedResidualFieldsEnabled ? RESIDUAL_FIELD_LABELS.RESIDUAL_PERCENT : undefined
        );
        if (!programResidualPercentResult.isValid) {
            newErrors.programResidualPercent = programResidualPercentResult.error;
        }

        return newErrors;
    }, [residualData, isUnifiedResidualFieldsEnabled]);

    return (
        <ResidualBreakdown
            currentData={residualData}
            errors={residualErrors}
            isLeaseResidualTreatmentEnabled={isLeaseResidualTreatmentEnabled}
            displayedResidualType={displayedResidualType}
            onResidualTypeChange={handleResidualTypeChange}
            residualControlsDisabled={residualControlsDisabled}
            restrictToDisplayedResidualUnit={isExplicitLenderP1Enabled}
            isUnifiedResidualFieldsEnabled={isUnifiedResidualFieldsEnabled}
        />
    );
};

export default ResidualBreakdownTabContent;
